import { useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Truck, 
  MapPin, 
  Calendar, 
  Search,
  ArrowRight,
  RefreshCw,
  Eye
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Skeleton } from "@/components/ui/skeleton";

interface CarrierListing {
  id: string;
  carrier_name: string;
  departure_city: string;
  arrival_city: string;
  stopover_cities: string | null;
  departure_date: string;
  frequency: string;
}

const AnnoncesTransporteurs = () => {
  const [searchTerm, setSearchTerm] = useState("");

  const { data: listings, isLoading, error } = useQuery({
    queryKey: ["carrier-listings-public"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("carrier_listings")
        .select("id, carrier_name, departure_city, arrival_city, stopover_cities, departure_date, frequency")
        .eq("status", "approved")
        .order("departure_date", { ascending: true });

      if (error) throw error;
      return data as CarrierListing[];
    },
  });

  const filteredListings = listings?.filter((listing) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      listing.departure_city.toLowerCase().includes(searchLower) ||
      listing.arrival_city.toLowerCase().includes(searchLower) ||
      listing.carrier_name.toLowerCase().includes(searchLower) ||
      (listing.stopover_cities?.toLowerCase().includes(searchLower) ?? false)
    );
  });

  const getFrequencyLabel = (frequency: string) => {
    const labels: Record<string, string> = {
      ponctuel: "Ponctuel",
      hebdomadaire: "Hebdomadaire",
      mensuel: "Mensuel",
      regulier: "Régulier",
    };
    return labels[frequency] || frequency;
  };

  return (
    <>
      <Helmet>
        <title>Annonces Transporteurs France-Maroc | GoBledPro</title>
        <meta
          name="description"
          content="Consultez les annonces de transporteurs disponibles pour vos envois France-Maroc. Trouvez le transporteur idéal pour vos colis et bagages."
        />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        <Header />

        <main className="flex-1 py-8 md:py-12">
          <div className="container mx-auto px-4">
            {/* Header Section */}
            <div className="text-center mb-8 md:mb-12">
              <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
                <Truck className="w-4 h-4 mr-2" />
                Transporteurs disponibles
              </Badge>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
                Annonces Transporteurs
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Trouvez un transporteur vérifié pour vos envois France-Maroc. 
                Consultez les trajets disponibles et contactez directement le transporteur.
              </p>
            </div>

            {/* Search Section */}
            <div className="max-w-2xl mx-auto mb-8">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Rechercher par ville de départ, d'arrivée ou nom du transporteur..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 py-6 text-base"
                />
              </div>
            </div>

            {/* Loading State */}
            {isLoading && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Card key={i} className="overflow-hidden">
                    <CardContent className="p-5">
                      <Skeleton className="h-6 w-3/4 mb-3" />
                      <Skeleton className="h-4 w-1/2 mb-2" />
                      <Skeleton className="h-4 w-2/3 mb-4" />
                      <Skeleton className="h-10 w-full" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="text-center py-12">
                <div className="bg-destructive/10 text-destructive p-6 rounded-lg max-w-md mx-auto">
                  <p className="font-medium">
                    Les annonces ne sont pas encore accessibles au public.
                  </p>
                  <p className="text-sm mt-2 opacity-80">
                    Cette fonctionnalité sera bientôt disponible.
                  </p>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !error && filteredListings?.length === 0 && (
              <div className="text-center py-12">
                <div className="bg-muted p-8 rounded-lg max-w-md mx-auto">
                  <Truck className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Aucune annonce disponible</h3>
                  <p className="text-muted-foreground text-sm">
                    {searchTerm
                      ? "Aucun trajet ne correspond à votre recherche."
                      : "Les transporteurs n'ont pas encore publié d'annonces."}
                  </p>
                </div>
              </div>
            )}

            {/* Listings Grid - Simplified Cards */}
            {!isLoading && !error && filteredListings && filteredListings.length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredListings.map((listing) => (
                  <Card 
                    key={listing.id} 
                    className="overflow-hidden hover:shadow-lg transition-all duration-300 border-2 hover:border-primary/30"
                  >
                    <CardContent className="p-5 space-y-4">
                      {/* Carrier Name & Frequency */}
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-lg font-heading font-bold text-foreground truncate">
                          {listing.carrier_name}
                        </h3>
                        <Badge variant="secondary" className="text-xs flex-shrink-0">
                          <RefreshCw className="w-3 h-3 mr-1" />
                          {getFrequencyLabel(listing.frequency)}
                        </Badge>
                      </div>
                      
                      {/* Route */}
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
                        <span className="font-semibold">{listing.departure_city}</span>
                        <ArrowRight className="w-4 h-4 text-muted-foreground" />
                        <span className="font-semibold">{listing.arrival_city}</span>
                      </div>
                      
                      {listing.stopover_cities && (
                        <p className="text-xs text-muted-foreground pl-6">
                          via {listing.stopover_cities}
                        </p>
                      )}

                      {/* Date */}
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {format(new Date(listing.departure_date), "d MMMM yyyy", { locale: fr })}
                        </span>
                      </div>

                      {/* View Details Button */}
                      <Button asChild className="w-full mt-2">
                        <Link to={`/annonce/${listing.id}`}>
                          <Eye className="w-4 h-4 mr-2" />
                          Voir les détails
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Info Banner */}
            <div className="mt-12 bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 rounded-xl p-6 text-center">
              <h3 className="font-heading font-bold text-lg mb-2">
                Vous êtes transporteur ?
              </h3>
              <p className="text-muted-foreground mb-4">
                Publiez votre annonce gratuitement et recevez des demandes de clients.
              </p>
              <Button asChild>
                <Link to="/nouvelle-annonce-transporteur">
                  <Truck className="w-4 h-4 mr-2" />
                  Publier une annonce
                </Link>
              </Button>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default AnnoncesTransporteurs;
